import { MultiChannelAudioUploadFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { useState } from 'react';
import { Spinner, Typography, Button } from '@scale/scaleui';
import { useMutation, useQuery } from 'react-query';
import {
  combineAudio,
  getAudioSource,
  processAudioAttachment,
  splitAudioChannels,
} from 'react-scale/lib/chat-api/multimodal';
import {
  SupportedAudioFormat,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
  formatMaybeErrorForLogging,
} from '@scale/mm-shared';
import logger from 'react-scale/lib/logging';
import retry from '@scale/shared/utils/retry';
import useCDSMultipartUpload from 'react-scale/components/scalers/tasks/textcollection/hooks/useCDSMultipartUpload';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import apiFetch from 'react-scale/lib/api-fetch';
import { decodeAudioBuffer } from '@scale/frontend-lib/audio/decodeAudio';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface CombineAudioSectionProps {
  annotation: MultiChannelAudioUploadFieldAnnotation;
  setAnnotation: (annotation: MultiChannelAudioUploadFieldAnnotation) => void;
  file1Url: string;
  file2Url: string;
  setFile1Url: (url: string | undefined) => void;
  setFile2Url: (url: string | undefined) => void;
  readonly?: boolean;
  assignmentContext: {
    assignmentId?: string;
    workerId?: string;
    projectId?: string;
  };
}

const CombineAudioSection = ({
  annotation,
  setAnnotation,
  file1Url,
  file2Url,
  setFile1Url,
  setFile2Url,
  readonly,
  assignmentContext,
}: CombineAudioSectionProps) => {
  const [combinedAudioUrl, setCombinedAudioUrl] = useState<string | undefined>(
    annotation.response[0].combinedFileUrl,
  );
  const fieldFormat = (annotation.response?.[0]?.mimeType.split('/')[1] ??
    'wav') as SupportedAudioFormat;
  const combinedFileName = `combinedAudio.${fieldFormat}`;

  const { assignmentId, workerId, projectId } = assignmentContext;
  const multipartUploadHook = useCDSMultipartUpload(assignmentId, workerId);

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId },
    false,
  );

  const transcriptionService = shouldUseWhisperTranscription
    ? createWhisperTranscriptionService()
    : createAwsTranscriptionService();

  const {
    mutate: handleCombineAudio,
    isLoading: isCombiningAudio,
    isError: isCombineAudioError,
  } = useMutation(
    ['multiChannelAudioCombine', file1Url, file2Url],
    async (): Promise<string | null> => {
      const { combinedAudioUrl } = await combineAudio({
        audioUrl1: file1Url,
        audioUrl2: file2Url,
        assignmentId,
        workerId,
        projectId,
      });
      setCombinedAudioUrl(combinedAudioUrl);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...annotation.response[0],
            combinedFileUrl: combinedAudioUrl,
            combinedFileName,
          },
        ],
      });
      return combinedAudioUrl;
    },
  );

  const {
    data: combinedAudioBlob,
    isLoading: isCombinedAudioBlobLoading,
    isError: isCombinedAudioBlobError,
  } = useQuery(
    ['getAudioBlob', combinedAudioUrl ?? ''],
    async (): Promise<Blob | null> => {
      if (!combinedAudioUrl) {
        return null;
      }
      const { source } = await getAudioSource({ audioUrl: combinedAudioUrl });

      const data = await fetch(source);

      if (!data.ok) {
        throw new Error('Failed to fetch audio blob');
      }

      return await data.blob();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const { mutate: handleFinishEditing, isLoading: isFinishingEditing } = useMutation(
    ['multiChannelAudioFinishEditing', combinedAudioUrl ?? ''],
    async (blob: Blob): Promise<MultiChannelAudioUploadFieldAnnotation | null> => {
      if (!blob) {
        return null;
      }

      const mimeType = `audio/${fieldFormat}`;

      // using the desired mime type since CDS doesn't have a way to change that after conversion actually happens (yet)
      const file = new File(
        [blob], // fileBits
        combinedFileName,
        {
          type: mimeType,
        },
      );

      const audioBuffer = await decodeAudioBuffer(await blob.arrayBuffer());

      // CDS multipart upload also converts file format
      // todo: add from/to formats
      const { cdsUrl } = await multipartUploadHook.upload({
        file,
        fileType: fieldFormat,
      });

      // split audio channels
      const { leftChannelAudioUrl, rightChannelAudioUrl } = await splitAudioChannels({
        audioUrl: cdsUrl,
        assignmentId,
        workerId,
        projectId,
      });

      const { url: file1Url } = await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
        method: 'POST',
        body: {
          url: leftChannelAudioUrl,
        },
      });
      setFile1Url(file1Url);

      const { url: file2Url } = await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
        method: 'POST',
        body: {
          url: rightChannelAudioUrl,
        },
      });
      setFile2Url(file2Url);

      try {
        const combinedEntity = await retry(
          () =>
            processAudioAttachment({
              audioUrl: cdsUrl,
              audioFormat: fieldFormat,
              assignmentId,
              workerId,
              userGeneratedAudio: true,
              transcriptionService,
            }),
          { limit: 3 },
        );
        const leftChannelEntity = await retry(
          () =>
            processAudioAttachment({
              audioUrl: leftChannelAudioUrl,
              audioFormat: fieldFormat,
              assignmentId,
              workerId,
              userGeneratedAudio: true,
              transcriptionService,
            }),
          { limit: 3 },
        );
        const rightChannelEntity = await retry(
          () =>
            processAudioAttachment({
              audioUrl: rightChannelAudioUrl,
              audioFormat: fieldFormat,
              assignmentId,
              workerId,
              userGeneratedAudio: true,
              transcriptionService,
            }),
          { limit: 3 },
        );

        return {
          ...annotation,
          response: [
            {
              ...annotation.response[0],
              file1Url: leftChannelEntity.url,
              file2Url: rightChannelEntity.url,
              combinedFileUrl: combinedEntity.url,
              combinedFileName,
              duration: audioBuffer?.duration,
            },
          ],
        };
      } catch (error) {
        logger.error(`AudioRecordingFieldSection: Failed to start processing audio attachment`, {
          projectId,
          assignmentId,
          workerId,
          error: formatMaybeErrorForLogging(error),
        });
        // fallback if no processing was kicked off
        return {
          ...annotation,
        };
      }
    },
    {
      onSuccess: audioAnnotation => {
        if (audioAnnotation) {
          setAnnotation(audioAnnotation);
        }
      },
      onError: error => {
        logger.error('AudioRecordingFieldSection: Failed to upload audio recording', {
          projectId,
          assignmentId,
          workerId,
          error: formatMaybeErrorForLogging(error),
        });
      },
    },
  );

  return (
    <div className="flex flex-col gap-2">
      <Typography.Paragraph2 className="font-semibold">Combined Audio</Typography.Paragraph2>
      {isFinishingEditing || isCombinedAudioBlobLoading ? (
        <div className="flex gap-1 items-center justify-center">
          <Spinner size="xs" variant="neutral" />
          <Typography.Paragraph3>
            {isFinishingEditing ? 'Processing...' : 'Loading...'}
          </Typography.Paragraph3>
        </div>
      ) : combinedAudioBlob ? (
        <ChatAudioEditor
          originalBlob={combinedAudioBlob}
          onFinish={handleFinishEditing}
          readonly={readonly}
        />
      ) : (
        <div className="flex flex-row gap-2 items-center">
          <Button
            onClick={() => handleCombineAudio()}
            disabled={isCombiningAudio || isCombinedAudioBlobLoading}
          >
            Combine Audio
          </Button>
          {(isCombiningAudio || isCombinedAudioBlobLoading) && <Spinner size="sm" />}
          {(isCombineAudioError || isCombinedAudioBlobError) && <div>Error combining audio</div>}
        </div>
      )}
    </div>
  );
};

export default CombineAudioSection;
